import React, { useEffect, useMemo } from 'react';

import {
    Table,
    Box,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { DragDropContext } from 'react-beautiful-dnd';

import DraggableTr from './DraggableTr';
import DroppableTbody from './DroppableTbody';
import accessByStringDot from '../../tools/string-dot-accessor';
import './data-table.css';

const getTableStyles = () => ({
    border: '1px solid #dee2e6',
    borderRadius: '4px',
    overflow: 'auto',

    '&.isDisabled': {
        opacity: '0.5',
        pointerEvents: 'none',
    },
    '.dragged-item': {
        display: 'flex',
        background: '#d8eeff',
        border: '1px dashed #89cbff',
        borderRadius: '3px',
    },
    '.dragged-item .data-table__cell': {
        display: 'none',
    },
    '.dragged-item .data-table__cell_title': {
        display: 'block',
        maxWidth: 'none !important',
    },
});

const CELL_STYLES = {
    overflow: 'hidden',
};

export default function DataTable({
    data,
    ordering,
    onOrdering,
    schema,
    error,
    isPreviousData,
    isFetching,
    isDraggable,
    onDragEnd,
}) {
    function handleOrdering(e) {
        if (!onOrdering) {
            return;
        }

        const { value } = e.currentTarget.dataset;

        onOrdering(value);
    }

    useEffect(() => {
        if (!error?.message) {
            return;
        }

        notifications.show({
            title: `Ошибка ${error.status || ''}`,
            message: error.message,
            color: 'red',
        });
    }, [error]);

    const Tr = isDraggable ? DraggableTr : 'tr';

    const table = useMemo(() => {
        if (!data || !data.results) {
            return null;
        }

        const head = [];
        const body = [];

        const orderDirection = ordering?.includes('-') ? 'desc' : 'asc';

        head.push(
            <tr key="head">
                {schema.map((schemaItem) => (
                    <th
                        className="data-table__heading"
                        key={`head-${schemaItem.accessor}`}
                    >
                        {!!onOrdering && (
                            <button
                                type="button"
                                className={`data-table__ordering ${ordering?.includes(schemaItem.accessor) ? orderDirection : ''}`}
                                data-value={schemaItem.accessor}
                                onClick={handleOrdering}
                                aria-label={`ordering-${schemaItem.accessor}`}
                            />
                        )}
                        <span className="data-table__heading-title">
                            {schemaItem.title}
                        </span>
                    </th>
                ))}
            </tr>,
        );

        data.results.map((row, i) => {
            body.push(
                <Tr id={`body-${row.id}`} key={`body-${row.id}`} index={i}>
                    {schema.map((schemaItem) => {
                        const cellValue = schemaItem.accessor.includes('.') ? accessByStringDot(row, schemaItem.accessor) : row[schemaItem.accessor];
                        const cellStyle = {
                            ...CELL_STYLES,
                            ...(schemaItem.style || {}),
                        };

                        if (schemaItem.render) {
                            return (
                                <td
                                    key={`body-${schemaItem.accessor}`}
                                    className={`data-table__cell data-table__cell_${schemaItem.type}`}
                                    style={cellStyle}
                                >
                                    {schemaItem.render({ currentData: cellValue, row, index: i })}
                                </td>
                            );
                        }

                        return (
                            <td
                                key={`body-${schemaItem.accessor}`}
                                className={`data-table__cell data-table__cell_${schemaItem.type}`}
                                style={cellStyle}
                            >
                                {cellValue}
                            </td>
                        );
                    })}
                </Tr>,
            );
        });

        return (
            <>
                <thead>{head}</thead>
                {isDraggable ? (
                    <DroppableTbody id="droppable-table">
                        {body}
                    </DroppableTbody>
                ) : (
                    <tbody>
                        {body}
                    </tbody>
                )}
            </>
        );
    }, [isDraggable, data, schema, ordering]);

    return (
        <div>
            <Box
                className={isPreviousData || isFetching ? 'isDisabled' : ''}
                sx={getTableStyles}
            >
                <Table
                    striped
                    fontSize="md"
                    verticalSpacing="xs"
                    horizontalSpacing="xs"
                    highlightOnHover
                    className="data-table"
                >
                    {isDraggable ? (
                        <DragDropContext onDragEnd={onDragEnd}>
                            {table}
                        </DragDropContext>
                    ) : (
                        table
                    )}
                </Table>
            </Box>
        </div>
    );
}
